import { Injectable } from '@angular/core';
import { AutoSub, AutoSubOnDestroy } from './auto-sub';
import { LocalStorage } from 'ngx-webstorage';
import { switchMap } from 'rxjs/operators';

import { MotesLoginService, UserRights } from '../motes-login';
import { MotesMediaPlayerService } from '../motes-media-player/media-player.service';
import { MediaEvent, MediaState, MediaType, Mote } from '../models';

export interface ActiveAudio { tourKey: string, storyKey: string, mediaKey: string, progress: number, duration: number };
interface ActiveTour { projectKey: string, tourKey: string, previewKey: string };

@AutoSubOnDestroy()
@Injectable({ providedIn: 'root' })
export class UserStateService {

    @LocalStorage() private activeAudio: { [userKey: string]: ActiveAudio };
    @LocalStorage() private activeTour: ActiveTour;
    @LocalStorage() public isWelcomeFlowSeen: boolean;

    private userRights: UserRights;

    constructor(
        private loginService: MotesLoginService,
        private mediaPlayerService: MotesMediaPlayerService) { }

    public init() {

        AutoSub(this).reg['userSub'] = this.loginService.watchUserRights().pipe(switchMap((userRights) => {
            this.userRights = userRights;
            return this.mediaPlayerService.watchMediaEvents();
        })).subscribe((ev) => {
            this.storeActiveAudio(ev);
        })
    }

    private storeActiveAudio(ev: MediaEvent) {
        const isAudioPlaying = (ev.media?.type == MediaType.SOUND || ev.media?.type == MediaType.AMBISONIC) && (ev.state == MediaState.PLAYING || ev.state == MediaState.PAUSED);
        if(!isAudioPlaying) return;
        // Skipping audio results in duration momentarily being 0. Do not store in this case
        if(ev.duration <= 0) return;
        // Check if the audio event is from the current active tour
        const isActiveTourPlaying = (this.activeTour?.tourKey == ev.mote.parent);
        // Do nothing if the audio is not from the current active tour (ie. avoid showing sneak peak audio as progress for example)
        if(!isActiveTourPlaying) return;

        // Init active audio map
        if(!this.activeAudio) this.activeAudio = {};
        // Only store the same audio if the progress differs with more than 3 seconds
        const currentAudio = this.activeAudio[this.userRights.uid];
        if(currentAudio) {
            const audioChanged = currentAudio.mediaKey != ev.media.key;
            const currentProgress = (currentAudio.progress / 100) * currentAudio.duration;
            const newProgress = (ev.progress / 100) * ev.duration;
            const progressDiff = Math.abs(currentProgress - newProgress);
            if(!audioChanged && (progressDiff < 3)) return;
        }

        // Update or add user's active audio
        this.activeAudio[this.userRights.uid] = {
            tourKey:  ev.mote.parent,
            storyKey: ev.mote.key,
            mediaKey: ev.media.key,
            progress: ev.progress || 0,
            duration: ev.duration || 0
        }

        // Update local storage map
        this.activeAudio = { ...this.activeAudio };
    }

    /**
     * Clear user's active audio
     */
    public clearActiveAudio(userKey: string): void {
        if(!userKey) return;
        delete this.activeAudio[userKey];
        this.activeAudio = { ...this.activeAudio };
    }

    /**
     * Get current user's active audio
     */
    public async getStoredActiveAudio(): Promise<ActiveAudio> {
        const currentUserKey = await this.getCurrentUserKey();
        // Find audio belonging to user if any
        return this.activeAudio ? this.activeAudio[currentUserKey] : null;
    }

    /**
     * Reset current user's active audio with the input story
     */
    public async resetActiveAudio(story: Mote) {
        const currentUserKey = await this.getCurrentUserKey();

        if(!this.activeAudio) this.activeAudio = {};
        this.activeAudio[currentUserKey] = {
            tourKey: story.parent,
            storyKey: story.key,
            mediaKey: (story.mediaArray.length > 0) ? story.mediaArray[0].key : '',
            progress: 0,
            duration: 0
        }

        // Update local storage map
        this.activeAudio = { ...this.activeAudio };
    }

    private async getCurrentUserKey() {
        if(this.userRights?.uid) return this.userRights.uid;
        // If user hasn't been found yet
        const loginRsp = await this.loginService.nextLoginResponse();
        return loginRsp.userRights.uid;
    }
    
    public storeActiveTour(projectKey: string, tourKey: string, previewKey: string) {
        this.activeTour = { projectKey: (projectKey || ''), tourKey: (tourKey || ''), previewKey: (previewKey || '') };
    }

    public getStoredActiveTour(): ActiveTour {
        return this.activeTour || { projectKey: '', tourKey: '', previewKey: '' };
    }

    public clearStoredActiveTour() {
        this.activeTour = { projectKey: '', tourKey: '', previewKey: '' };
    }

}
